import { useState } from 'react'
import clsx from 'clsx'
import MetricsGrid from './MetricsGrid'
import KAMImplementationSection from './KAMImplementationSection'

const getOperatorColorHex = (color) => {
  const colors = {
    orange: '#FF7900',
    tmobile: '#E20074',
    plus: '#00A651',
    play: '#9333EA',
  }
  return colors[color] || '#6b7280'
}

const getStatusBadgeClass = (status) => {
  switch (status) {
    case 'good':
      return 'text-status-green bg-status-green-light'
    case 'medium':
      return 'text-status-yellow bg-status-yellow-light'
    case 'bad':
      return 'text-status-red bg-status-red-light'
    case 'na':
      return 'text-blue-600 bg-blue-100'
    default:
      return 'text-gray-600 bg-gray-100'
  }
}

const getStatusText = (status) => {
  switch (status) {
    case 'good':
      return 'DOBRZE'
    case 'medium':
      return 'ŚREDNIO'
    case 'bad':
      return 'ŹLE'
    case 'na':
      return 'N/A'
    case 'loading':
      return '...'
    default:
      return 'BRAK DANYCH'
  }
}

const getImplementationStatus = (visData) => {
  if (!visData?.status) return null
  
  const status = visData.status
  if (status === 'DOSKONAŁY') {
    return { text: 'Status implementacji: DOSKONAŁY', color: 'text-green-600', icon: '⭐' }
  }
  if (status === 'BARDZO DOBRY') {
    return { text: 'Status implementacji: BARDZO DOBRY', color: 'text-status-green', icon: '✓' }
  }
  if (status === 'W TRAKCIE') {
    return { text: 'Status implementacji: W TRAKCIE', color: 'text-status-yellow', icon: '⚠' }
  }
  return { text: `Status implementacji: ${status}`, color: 'text-gray-600', icon: '📊' }
}

// Small loading indicator for metrics with shimmer effect
const MetricLoadingIndicator = () => (
  <span className="inline-block relative overflow-hidden w-12 h-5 bg-gray-200 rounded">
    <span 
      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
      style={{ animation: 'shimmer 1.5s infinite', transform: 'translateX(-100%)' }}
    />
  </span>
)

const OperatorCard = ({ 
  operator, 
  stockExpositionData,
  salonAttributesData,
  visualizationData,
  helpers,
  loadingGoals = false,
  loadingTrends = false
}) => {
  const { getCoverageLabel, getCoverage30dLabel, getCoverageValue, getGoalColor, getAvgSales } = helpers
  const [groupForecastExpanded, setGroupForecastExpanded] = useState(false)
  const visData = visualizationData?.operator_status_table?.[operator.code]
  const attrData = salonAttributesData?.[operator.code]
  const implementationStatus = getImplementationStatus(visData)
  
  // Check if this is placeholder data (before operatorSummaries loads)
  const isPlaceholder = operator.isPlaceholder
  
  // Check if enhancement data is still loading
  const isLoadingEnhancements = loadingGoals || loadingTrends

  // Get target color (hex) based on target percentage
  const getTargetColorHex = (pct) => {
    if (pct >= 100) return '#16a34a'
    if (pct >= 80) return '#2563eb'
    if (pct >= 60) return '#d97706'
    return '#dc2626'
  }

  // Get goal color (hex) based on performance
  const getGoalColorHex = () => {
    switch (operator.performance.type) {
      case 'success': return '#16a34a'
      case 'info': return '#2563eb'
      case 'warning': return '#d97706'
      case 'error': return '#dc2626'
      default: return '#1f2937'
    }
  }

  // Helper to show loading indicator for null values
  const valueOrLoading = (val) => {
    if (val === null || val === undefined) {
      return <MetricLoadingIndicator />
    }
    return val
  }

  // Build metrics for row 1: Sales 7d, 30d
  const row1Metrics = [
    {
      label: 'Sprzedaż 7 Dni',
      value: valueOrLoading(operator.realSales7d),
      tooltip: 'Łączna liczba sprzedanych urządzeń w ostatnich 7 dniach'
    },
    {
      label: 'Sprzedaż 30d',
      value: isPlaceholder ? <MetricLoadingIndicator /> : (operator.operatorData.total_sales_30d || 0),
      tooltip: 'Łączna sprzedaż w ostatnich 30 dniach'
    }
  ]

  // Forecast section data
  const perf = operator.performance
  const hasForecastBars = !isLoadingEnhancements && !isPlaceholder && perf.forecastPct != null && perf.forecastPctTarget != null

  // Per-group forecast entries (only available when goals and working days are loaded)
  const groupForecastData = (() => {
    if (isLoadingEnhancements || isPlaceholder) return null
    const wdTotal = operator.goalData?.working_days_total
    const wdElapsed = operator.goalData?.working_days_elapsed
    if (!wdTotal || !wdElapsed || !operator.goalData?.sales_breakdown) return null
    const entries = Object.entries(operator.goalData.sales_breakdown)
      .filter(([, d]) => typeof d === 'object' && (d.goal != null || d.goal_target != null))
    if (entries.length === 0) return null
    return { entries, scale: wdTotal / wdElapsed }
  })()
  const hasGroupForecast = !!groupForecastData

  // Build metrics for row 2: Coverage 14d, 30d, Requires Attention
  const coverageValue = getCoverageValue(operator)
  const row2Metrics = [
    {
      label: getCoverageLabel(operator),
      value: coverageValue === null ? <MetricLoadingIndicator /> : coverageValue,
      tooltip: 'Procent salonów odwiedzonych w ostatnich 14 dniach'
    },
    {
      label: getCoverage30dLabel(operator),
      value: isPlaceholder ? <MetricLoadingIndicator /> : (
        operator.operatorData.kams_visiting 
          ? `${Math.round((operator.coverage30d || 0) * 100 * 10) / 10}%` 
          : 'N/A'
      ),
      tooltip: 'Pokrycie w ostatnich 30 dniach'
    },
    {
      label: 'Wymaga Uwagi',
      value: isPlaceholder ? <MetricLoadingIndicator /> : (operator.operatorData.nbv_special_attention_count || 0),
      tooltip: 'Liczba salonów wymagających szczególnej uwagi'
    }
  ]

  // Build metrics for row 3: Visits, Avg Sales
  const avgSalesValue = getAvgSales(operator)
  const row3Metrics = [
    {
      label: 'Wizyty 7 Dni',
      value: isPlaceholder ? <MetricLoadingIndicator /> : (operator.operatorData.total_visits_7d || 0),
      tooltip: 'Łączna liczba wizyt KAM-ów w ostatnich 7 dniach'
    },
    {
      label: 'Śr. Sprzedaż/Salon',
      value: avgSalesValue === null ? <MetricLoadingIndicator /> : avgSalesValue,
      tooltip: 'Średnia liczba sprzedanych urządzeń na salon w ostatnich 30 dniach'
    }
  ]

  return (
    <div className="bg-white rounded-xl border border-brand-gray-200 shadow-sm flex flex-col">
      {/* Header */}
      <div className="p-4 flex justify-between items-center border-b border-brand-gray-200">
        <span 
          className="px-3 py-1 text-sm font-semibold text-white rounded-full"
          style={{ backgroundColor: getOperatorColorHex(operator.color) }}
        >
          {operator.name}
        </span>
        <span className={clsx(
          "px-3 py-1 text-xs font-bold rounded-full",
          getStatusBadgeClass(operator.status)
        )}>
          {getStatusText(operator.status)}
        </span>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3 flex-grow">
        {/* Row 1: Sales metrics */}
        <MetricsGrid metrics={row1Metrics} columns={2} />

        {/* Per-product-group sales breakdown (driven by product_groups config) */}
        {operator.operatorData?.sales_breakdown && Object.keys(operator.operatorData.sales_breakdown).length > 0 && (
          <div className="pl-3 border-l-2 border-orange-200 space-y-1.5">
            <div className="flex items-center justify-between text-xs text-gray-400 mb-0.5">
              <span></span>
              <div className="flex gap-4">
                <span className="w-10 text-right">7 dni</span>
                <span className="w-10 text-right">30 dni</span>
              </div>
            </div>
            {Object.entries(operator.operatorData.sales_breakdown).map(([key, stats]) => (
              <div key={key} className="flex items-center justify-between text-xs text-gray-600">
                <span className="font-medium">{stats.name || key.replace(/_/g, ' ')}</span>
                <div className="flex gap-4">
                  <span className="w-10 text-right font-semibold">{stats.sales_7d}</span>
                  <span className="w-10 text-right">{stats.sales_30d}</span>
                </div>
              </div>
            ))}
            {/* Per-group goal realization mini bars */}
            {operator.goalData?.sales_breakdown && (() => {
              const entries = Object.entries(operator.goalData.sales_breakdown)
                .filter(([, d]) => typeof d === 'object' && d.goal != null)
              if (entries.length === 0) return null
              return (
                <div className="mt-2 pt-2 border-t border-gray-100 space-y-1.5">
                  <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">Realizacja MTD vs cel minimum</div>
                  {entries.map(([name, data]) => {
                    const pct = data.goal > 0 ? Math.min((data.mtd / data.goal) * 100, 100) : 0
                    const color = pct >= 80 ? '#f59e0b' : pct >= 60 ? '#f97316' : '#ef4444'
                    return (
                      <div key={name}>
                        <div className="flex justify-between text-xs text-gray-500 mb-0.5">
                          <span>{name}</span>
                          <span className="font-medium" style={{ color }}>
                            {pct.toFixed(0)}%
                            <span className="text-gray-400 font-normal ml-1">
                              ({Math.round(data.mtd)}/{Math.round(data.goal)})
                            </span>
                            {data.margin != null && (
                              <span className="text-green-600 font-normal ml-1">
                                {data.margin.toLocaleString('pl-PL')} PLN
                              </span>
                            )}
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-1.5">
                          <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })()}
          </div>
        )}

        {/* Forecast Progress Bars — prominent section */}
        {(isLoadingEnhancements || isPlaceholder) ? (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="text-xs text-gray-500 mb-2 font-medium">Prognoza vs Cel</div>
            <MetricLoadingIndicator />
          </div>
        ) : hasForecastBars ? (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500 font-medium">Prognoza vs Cel</span>
              {hasGroupForecast && (
                <button
                  type="button"
                  onClick={() => setGroupForecastExpanded(v => !v)}
                  className="w-7 h-7 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-200 transition-colors"
                  aria-expanded={groupForecastExpanded}
                  aria-label={groupForecastExpanded ? 'Ukryj grupy produktowe' : 'Pokaż grupy produktowe'}
                  title={groupForecastExpanded ? 'Ukryj grupy produktowe' : 'Pokaż grupy produktowe'}
                >
                  <svg
                    className={clsx("w-5 h-5 transition-transform", groupForecastExpanded && "rotate-180")}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-14 shrink-0 font-medium">Minimum</span>
                <div className="flex-1 h-4 bg-white rounded-full overflow-hidden border border-gray-200">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(perf.forecastPct, 100)}%`,
                      backgroundColor: getTargetColorHex(perf.forecastPct)
                    }}
                  />
                </div>
                <span
                  className="text-base font-extrabold w-16 text-right shrink-0"
                  style={{ color: getTargetColorHex(perf.forecastPct) }}
                >
                  {perf.forecastPct.toFixed(0)}%
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 w-14 shrink-0 font-medium">Target</span>
                <div className="flex-1 h-4 bg-white rounded-full overflow-hidden border border-gray-200">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.min(perf.forecastPctTarget, 100)}%`,
                      backgroundColor: getTargetColorHex(perf.forecastPctTarget)
                    }}
                  />
                </div>
                <span
                  className="text-base font-extrabold w-16 text-right shrink-0"
                  style={{ color: getTargetColorHex(perf.forecastPctTarget) }}
                >
                  {perf.forecastPctTarget.toFixed(0)}%
                </span>
              </div>
            </div>
            {perf.detail && (
              <p className="text-xs text-gray-500 mt-2">{perf.detail}</p>
            )}
          </div>
        ) : (
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500 font-medium">Prognoza vs Cel</span>
              {hasGroupForecast && (
                <button
                  type="button"
                  onClick={() => setGroupForecastExpanded(v => !v)}
                  className="w-7 h-7 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 hover:bg-gray-200 transition-colors"
                  aria-expanded={groupForecastExpanded}
                  aria-label={groupForecastExpanded ? 'Ukryj grupy produktowe' : 'Pokaż grupy produktowe'}
                  title={groupForecastExpanded ? 'Ukryj grupy produktowe' : 'Pokaż grupy produktowe'}
                >
                  <svg
                    className={clsx("w-5 h-5 transition-transform", groupForecastExpanded && "rotate-180")}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
            </div>
            <div className="font-extrabold text-lg" style={{ color: getGoalColorHex() }}>
              {perf.value || 'N/A'}
            </div>
            {perf.detail && <p className="text-xs text-gray-500 mt-1">{perf.detail}</p>}
          </div>
        )}

        {/* Per-group Prognoza vs Cel (Minimum / Target) */}
        {hasGroupForecast && groupForecastExpanded && (() => {
          const { entries, scale } = groupForecastData
          return (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="text-xs text-gray-500 mb-2 font-medium">Prognoza końca miesiąca vs cel (wg grup)</div>
              <div className="space-y-3">
                {entries.map(([name, data]) => {
                  const forecast = (data.mtd || 0) * scale
                  const goalMin = data.goal
                  const goalTarget = data.goal_target ?? data.goal
                  const pctMin = goalMin && goalMin > 0 ? (forecast / goalMin) * 100 : null
                  const pctTarget = goalTarget && goalTarget > 0 ? (forecast / goalTarget) * 100 : null
                  return (
                    <div key={name}>
                      <div className="text-xs text-gray-600 font-medium mb-1">{name}</div>
                      <div className="space-y-1.5">
                        {pctMin != null && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-14 shrink-0">Minimum</span>
                            <div className="flex-1 h-3 bg-white rounded-full overflow-hidden border border-gray-200">
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${Math.min(pctMin, 100)}%`, backgroundColor: getTargetColorHex(pctMin) }}
                              />
                            </div>
                            <span
                              className="text-xs font-bold w-12 text-right shrink-0"
                              style={{ color: getTargetColorHex(pctMin) }}
                            >
                              {pctMin.toFixed(0)}%
                            </span>
                          </div>
                        )}
                        {pctTarget != null && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500 w-14 shrink-0">Target</span>
                            <div className="flex-1 h-3 bg-white rounded-full overflow-hidden border border-gray-200">
                              <div
                                className="h-full rounded-full transition-all duration-500"
                                style={{ width: `${Math.min(pctTarget, 100)}%`, backgroundColor: getTargetColorHex(pctTarget) }}
                              />
                            </div>
                            <span
                              className="text-xs font-bold w-12 text-right shrink-0"
                              style={{ color: getTargetColorHex(pctTarget) }}
                            >
                              {pctTarget.toFixed(0)}%
                            </span>
                          </div>
                        )}
                      </div>
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        prognoza: {Math.round(forecast)}
                        {goalMin != null && ` / min: ${Math.round(goalMin)}`}
                        {goalTarget != null && goalTarget !== goalMin && ` / target: ${Math.round(goalTarget)}`}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )
        })()}

        {/* Row 2: Coverage metrics */}
        <MetricsGrid metrics={row2Metrics} columns={3} />

        {/* Row 3: Visits & Avg Sales */}
        <MetricsGrid metrics={row3Metrics} columns={2} />

        {/* KAM Implementation + Operator Data Section (combined) */}
        <KAMImplementationSection 
          operator={operator}
          stockExpositionData={stockExpositionData}
          salonAttributesData={salonAttributesData}
          visualizationData={visualizationData}
        />

        {/* Premiowanie & Plan Sprzedaży with gray background */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-gray-100 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500 mb-1">Premiowanie</div>
            <div 
              className="font-bold text-lg"
              style={{ 
                color: visData?.premiowanie?.status === 'high' ? '#16a34a' :
                       visData?.premiowanie?.status === 'medium' ? '#ca8a04' : '#1f2937'
              }}
            >
              {visData?.premiowanie?.value || visData?.premiowanie || 'N/A'}
            </div>
          </div>
          <div className="bg-gray-100 rounded-lg p-3 text-center">
            <div className="text-xs text-gray-500 mb-1">Plan Sprzedaży</div>
            <div 
              className="font-bold text-lg"
              style={{ 
                color: (visData?.plan_sprzedazy === 'TAK' || visData?.plan_sprzedazy?.status === 'high') ? '#16a34a' :
                       visData?.plan_sprzedazy === 'NIE' ? '#dc2626' :
                       visData?.plan_sprzedazy?.status === 'medium' ? '#ca8a04' : '#1f2937'
              }}
            >
              {visData?.plan_sprzedazy?.value || visData?.plan_sprzedazy || 'N/A'}
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Implementation Status */}
      {implementationStatus && (
        <div className="mt-auto border-t border-brand-gray-200 bg-brand-gray-50 rounded-b-xl p-4">
          <p className={clsx("text-xs font-semibold text-center", implementationStatus.color)}>
            {implementationStatus.icon} {implementationStatus.text}
          </p>
        </div>
      )}
    </div>
  )
}

export default OperatorCard

